'use client'

import type maplibregl from 'maplibre-gl'
import React, { useEffect, useRef } from 'react'

import { useMap } from '@/components/ui/map'

const containerSizeCache = new WeakMap<HTMLElement, { h: number; w: number }>()
const CONTAINER_WIDTH_CHANGE_EPSILON_PX = 1

function readContainerSize(container: HTMLElement): { h: number; w: number } {
  return { h: container.clientHeight, w: container.clientWidth }
}

export function hasContainerWidthChanged(container: HTMLElement): boolean {
  const previousSize = containerSizeCache.get(container)
  if (!previousSize) return true

  return Math.abs(previousSize.w - container.clientWidth) > CONTAINER_WIDTH_CHANGE_EPSILON_PX
}

export function hasContainerSizeChanged(container: HTMLElement): boolean {
  const previousSize = containerSizeCache.get(container)
  if (!previousSize) return true

  const { h, w } = readContainerSize(container)
  return (
    Math.abs(previousSize.w - w) > CONTAINER_WIDTH_CHANGE_EPSILON_PX ||
    Math.abs(previousSize.h - h) > CONTAINER_WIDTH_CHANGE_EPSILON_PX
  )
}

export function syncContainerSizeCache(container: HTMLElement) {
  containerSizeCache.set(container, readContainerSize(container))
}

/** Skip programmatic camera refit while the user is pinching/panning, unless the view/route changed. */
export function shouldSkipCameraFit(args: {
  isFirstLoad: boolean
  isRouteChange: boolean
  isViewChange: boolean
  preserveUserZoom: boolean
}): boolean {
  if (args.isFirstLoad || args.isViewChange || args.isRouteChange) {
    return false
  }

  return args.preserveUserZoom
}

type LocationMapContainerResizeObserverArgs = {
  isCameraUpdateRef: React.RefObject<boolean>
  isLoaded: boolean
  map: maplibregl.Map | null
  onRefitRef: React.MutableRefObject<() => void>
  preserveUserZoom: boolean
}

/** Observes map container size without reconnecting when preserveUserZoom toggles. */
export function useLocationMapContainerResizeObserver({
  isCameraUpdateRef,
  isLoaded,
  map,
  onRefitRef,
  preserveUserZoom,
}: LocationMapContainerResizeObserverArgs) {
  const preserveUserZoomRef = useRef(preserveUserZoom)
  preserveUserZoomRef.current = preserveUserZoom

  useEffect(() => {
    if (!map || !isLoaded) return

    const container = map.getContainer()

    const resizeObserver = new ResizeObserver(() => {
      if (isCameraUpdateRef.current) return
      if (map.isMoving()) return
      if (preserveUserZoomRef.current || !hasContainerWidthChanged(container)) {
        map.resize()
        syncContainerSizeCache(container)
        return
      }

      onRefitRef.current()
    })

    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [isCameraUpdateRef, isLoaded, map, onRefitRef])
}

export function LocationMapNavigationHandlers({ onUserNavigate }: { onUserNavigate: () => void }) {
  const { isLoaded, map } = useMap()

  useEffect(() => {
    if (!map || !isLoaded) return

    map.touchZoomRotate.disableRotation()
    map.dragRotate.disable()
    map.touchPitch.disable()
    map.scrollZoom.disable()

    const handleMoveStart = (event: { originalEvent?: Event }) => {
      if (event.originalEvent) {
        onUserNavigate()
      }
    }

    map.on('movestart', handleMoveStart)

    return () => {
      map.off('movestart', handleMoveStart)
    }
  }, [isLoaded, map, onUserNavigate])

  return null
}
